import {
  config,
  formatMeetingPrepLeadTime,
  MEETING_PREP_LEAD_TIME_PRESETS_MINUTES,
  resolveMeetingAssistEnabled,
  resolveMeetingIntelligenceEnabled,
  useUserConfig,
} from '@littlebird/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { MultiSelectSearchableDropdown } from '@/components/ui/MultiSelectSearchableDropdown';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Switch } from '@/components/ui/Switch';
import TextArea from '@/components/ui/TextArea';
import { isDev } from '@/env';
import { electronApis } from '@/features/electron-api';
import { ShowMeetingStatusWindowOption } from '@/features/electron-api/features/MeetingNotesAPI';
import {
  FEATURES,
  PAYLOAD_FEATURES,
  useFeature,
  useFeaturePayload,
} from '@/features/feature-flags';
import { useConnectedAccountsQuery } from '@/features/integrations/integrations.query';
import { setMeetingAssistEnabled } from '@/features/meetings/assist/meeting-assist-setting';
import { useAutomaticMeetingPrepEnabled } from '@/features/meetings/automatic-meeting-prep.hooks';
import {
  groupMeetingAppsByName,
  resolveBaseMeetingApps,
} from '@/features/meetings/meeting-app-allowlist';
import { MEETING_PREFERENCE_INPUT_DEVICE_CONFIG } from '@/features/meetings/meetings-transcription-input-device.config';
import {
  MeetingNotificationFilter,
  MeetingReminderLeadTime,
  MeetingDetectionMode,
  MeetingMenubarIncludeEventsOption,
  MeetingMenubarPreviewLeadTimeOption,
  MeetingAutoStopTimeout,
  EventType,
  getMeetingDetectionModeLabel,
  getMeetingMenubarIncludeEventsDays,
  getMeetingMenubarIncludeEventsLabel,
  getMeetingMenubarPreviewLeadTimeLabel,
} from '@/features/meetings/meetings.types';
import { TranscriptionInputDeviceDropdown } from '@/features/transcription-input-device';
import { resolvePreferredTranscriptionEngine } from '@/lib/meetings/transcription-engine';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { AppCapability, isCapable } from '@/utils/capabilities';

import { domainSchema } from '../domain-input';
import { SettingsSection } from '../settings-section';

import {
  type MeetingPromptFields,
  MeetingPrepInstructionsTextArea,
  MeetingAssistInstructionsTextArea,
  MeetingSummaryInstructionsTextArea,
  useMeetingPromptFields,
} from './meeting-prompt-fields';
import { MeetingAssistPopupOptions } from './settings-meetings-assist-popups';
import { HiddenCirclesOption } from './settings-meetings-hidden-circles';
import { PreferredTranscriptionEngineOption } from './settings-meetings-local-transcription';

export const MeetingsSettings = observer(() => {
  const menubarCalendarEnabled = config.menubar.calendar_enabled;
  const meetingFields = useMeetingPromptFields();
  const canRecord = isCapable(AppCapability.RecordMeeting);
  // Reminders and the menu bar calendar are driven by the calendar, not by a recording, so they
  // need only a desktop build - including macOS older than the recording minimum
  const onDesktop = isCapable(AppCapability.DesktopExclusive);

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Meetings</SettingsSection.Title>
      </SettingsSection.Header>

      <SettingsSection.Group title="Custom instructions">
        <SettingsSection.Item
          direction="vertical"
          searchId="meetings.prep-instructions"
        >
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>Meeting Prep</SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Personalize how Littlebird prepares you for upcoming meetings
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <MeetingPrepInstructionsTextArea
            className="min-h-[8rem] max-h-[16rem] text-sm resize-none [field-sizing:content]"
            fields={meetingFields}
          />
        </SettingsSection.Item>
        <SettingsSection.Seperator />
        <SettingsSection.Item
          direction="vertical"
          searchId="meetings.summary-instructions"
        >
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Meeting Summary
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Personalize how Littlebird summarizes your recorded meetings.
              These instructions override the default summary layout.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <MeetingSummaryInstructionsTextArea
            className="min-h-[8rem] max-h-[16rem] text-sm resize-none [field-sizing:content]"
            fields={meetingFields}
          />
        </SettingsSection.Item>
        <AssistInstructionsItem fields={meetingFields} />
      </SettingsSection.Group>

      <MeetingAssistGroup />
      <MeetingIntelligenceGroup />

      {/* Not gated: prep and both emails are produced server-side, so they apply without a recording client */}
      <SettingsSection.Group title="Meeting prep and emails">
        <AutomaticMeetingPrepOption />
        <MeetingPrepLeadTimeOption />
        <SettingsSection.Seperator />
        <MeetingPrepEmailOption />
        <SettingsSection.Seperator />
        <MeetingSummaryEmailOption />
      </SettingsSection.Group>

      {canRecord && (
        <SettingsSection.Group title="Transcription">
          <MeetingLanguagePreferenceOption />
          <SettingsSection.Seperator />
          <PreferredTranscriptionEngineOption />
          <PreferredMeetingInputDeviceOption />
          <CustomVocabularyOption />
        </SettingsSection.Group>
      )}

      {canRecord && (
        <SettingsSection.Group title="Recording">
          <MeetingDetectionModeOption />
          <SettingsSection.Seperator />
          <MeetingAutoStopTimeoutOption />
          <SettingsSection.Seperator />
          <ShowSummaryAfterAutoStopOption />
          <SettingsSection.Seperator />
          <MeetingDetectionAppsOption />
          <SettingsSection.Seperator />
          <MeetingDetectionSitesOption />
        </SettingsSection.Group>
      )}

      {onDesktop && (
        <SettingsSection.Group title="Reminders & notifications">
          <MeetingReminderOption />
          <SettingsSection.Seperator />
          <MeetingNotificationFilterOption />
        </SettingsSection.Group>
      )}

      {/* Not gated: these are query params of the calendar events fetch behind the "Coming up" list */}
      <SettingsSection.Group title="Calendar">
        <CalendarDaysAheadOption />
        <SettingsSection.Seperator />
        <CalendarMaxEventsOption />
        <SettingsSection.Seperator />
        <ExcludedCalendarEventTypesOption />
        <SettingsSection.Seperator />
        <ExcludedCalendarStatusesOption />
        <SettingsSection.Seperator />
        <ExcludeSoloEventsOption />
      </SettingsSection.Group>

      <SettingsSection.Group title="Sidebar">
        <HiddenCirclesOption />
      </SettingsSection.Group>

      {canRecord && (
        <SettingsSection.Group title="Floating widget">
          {isDev && (
            <>
              <MeetingStatusWindowOption />
              <SettingsSection.Seperator />
            </>
          )}
          <HideMeetingWidgetFromScreenShareOption />
        </SettingsSection.Group>
      )}

      {onDesktop && (
        <SettingsSection.Group title="Menu bar calendar">
          <MenubarCalendarEnabledOption />
          <SettingsSection.Seperator />
          <div
            className={
              menubarCalendarEnabled
                ? 'flex flex-col gap-5'
                : 'flex flex-col gap-5 opacity-50 pointer-events-none'
            }
          >
            <MenubarIncludeEventsOptionField />
            <SettingsSection.Seperator />
            <MenubarPreviewLeadTimeOptionField />
          </div>
        </SettingsSection.Group>
      )}
    </SettingsSection.Root>
  );
});

const MeetingStatusWindowOption = observer(() => {
  const userConfig = useUserConfig();
  const current = config.meetings.status_window_option;
  const selectedOption =
    current === ShowMeetingStatusWindowOption.Always ? 'Always' : 'Automatic';

  return (
    <SettingsSection.Item searchId="meetings.status-window">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Floating Meeting Widget
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Show the meeting widget always or only during meetings
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{selectedOption}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[10rem] gap-2 mt-1"
        >
          <DropdownMenu.Item
            onSelect={() =>
              userConfig.meetings.set(
                'status_window_option',
                ShowMeetingStatusWindowOption.Always
              )
            }
          >
            Always
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() =>
              userConfig.meetings.set(
                'status_window_option',
                ShowMeetingStatusWindowOption.Automatic
              )
            }
          >
            Automatic
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const HideMeetingWidgetFromScreenShareOption = observer(() => {
  const userConfig = useUserConfig();
  return (
    <SettingsSection.Item searchId="meetings.hide-widget-screen-share">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Hide Widget From Screen Sharing
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Keep the floating meeting widget invisible to screen capture and
          shared screens
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Switch
        checked={config.privacy.hide_meeting_widget_from_screen_share}
        onCheckedChange={(v) =>
          userConfig.privacy.set('hide_meeting_widget_from_screen_share', v)
        }
      />
    </SettingsSection.Item>
  );
});

const MeetingNotificationFilterOption = observer(() => {
  const userConfig = useUserConfig();
  const selectedFilter =
    config.meetings.notification_filter ===
    MeetingNotificationFilter.ConfirmedOnly
      ? 'Confirmed meetings only'
      : 'All meetings';

  return (
    <SettingsSection.Item searchId="meetings.notification-filter">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Notify me about</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Which upcoming meetings trigger a notification
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{selectedFilter}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[14rem] gap-2 mt-1"
        >
          <DropdownMenu.Item
            onSelect={() =>
              userConfig.meetings.set(
                'notification_filter',
                MeetingNotificationFilter.ConfirmedOnly
              )
            }
          >
            Confirmed meetings only
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() =>
              userConfig.meetings.set(
                'notification_filter',
                MeetingNotificationFilter.AllMeetings
              )
            }
          >
            All meetings
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const MeetingLanguagePreferenceOption = observer(() => {
  const user = userStore.user;

  const [searchTerm, updateSearchTerm] = useState('');
  const [languageCodeToConfirm, requestLanguageCodeConfirmation] = useState<
    string | null
  >(null);
  const [displayConfrimationAlert, requestConfirmationAlertDisplaying] =
    useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { data: languages = [], isLoading } = useQuery({
    queryKey: ['fetch-meeting-languages'],
    queryFn: () => backendApis.meetingNotes.fetchMeetingLanguages(),
  });

  const { mutate: updateLanguage, isPending: needsConfirmation } = useMutation({
    mutationKey: ['update-meeting-language-preference'],
    mutationFn: async (languageCode: string) =>
      backendApis.meetingNotes.updateMeetingLanguagePreference(languageCode),
    onSuccess: (response) => {
      userStore.updateUser({
        meeting_language_preference: response.meeting_language_preference,
      });
      toast.success('Meeting language updated', {
        description:
          'Start a meeting for this change to take effect. If one is running, restart it.',
      });
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: {
          data?: {
            detail?: {
              user_error_message?: string;
            };
          };
        };
        message?: string;
      };

      toast.error('Failed to update meeting language', {
        description:
          apiError.response?.data?.detail?.user_error_message ??
          apiError.message,
      });
    },
  });

  const currentCode = user?.meeting_language_preference ?? 'en';
  const currentLanguage =
    languages.find((language) => language.code === currentCode) ?? languages[0];

  const languageName = currentLanguage?.name ?? 'Select language';
  const selectionLabel = isLoading ? 'Loading...' : languageName;

  const filteredLanguages = languages.filter((language) =>
    language.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const cleanSearch = () => {
    updateSearchTerm('');
    requestLanguageCodeConfirmation(null);
    requestConfirmationAlertDisplaying(false);
  };

  const handleLanguageSelect = (languageCode: string) => {
    if (languageCode === currentCode) {
      return;
    }

    if (languageCode === 'multi') {
      requestLanguageCodeConfirmation(languageCode);
      requestConfirmationAlertDisplaying(true);

      return;
    }

    cleanSearch();
    updateLanguage(languageCode);
  };

  const handleLanguageConfirmation = () => {
    if (!languageCodeToConfirm) return;

    updateLanguage(languageCodeToConfirm);
    cleanSearch();
  };

  const handleOtherLanguageSelection = () => {
    cleanSearch();
  };

  return (
    <>
      <SettingsSection.Item searchId="meetings.spoken-language">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>Spoken language</SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Language used to transcribe what&apos;s said in meetings
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <span>{selectionLabel}</span>
            <Icons.chevronDown className="size-3" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            align="end"
            side="bottom"
            className="w-[14rem] mt-1 p-0 overflow-hidden"
          >
            <div className="p-2 pb-1">
              <input
                ref={searchInputRef}
                className="w-full rounded-md border border-border bg-surface px-2 py-1 text-xs outline-none"
                placeholder="Search language..."
                value={searchTerm}
                onChange={(event) => updateSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                }}
              />
            </div>
            <div
              className="max-h-[260px] overflow-y-auto p-1 pt-0"
              onWheel={(event) => event.stopPropagation()}
            >
              {filteredLanguages.map((language) => (
                <DropdownMenu.Item
                  key={language.code}
                  onSelect={() => handleLanguageSelect(language.code)}
                >
                  {language.name}
                </DropdownMenu.Item>
              ))}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </SettingsSection.Item>
      {displayConfrimationAlert && (
        <SettingsSection.Item>
          <div className="rounded-md border border-warning bg-warning/10 p-3 text-xs">
            <p className="text-foreground">
              {resolvePreferredTranscriptionEngine() === 'local'
                ? 'Multilingual mode supports only 8 languages - English, Spanish, French, German, Russian, Portuguese, Italian, and Dutch. If your meeting notes sessions are mono-lingual, please select a specific language for best results.'
                : 'Multilingual mode supports only 10 languages - English, Spanish, French, German, Hindi, Russian, Portuguese, Japanese, Italian, and Dutch. If your meeting notes sessions are mono-lingual, please select a specific language for best results.'}
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="default"
                size="xs"
                onClick={handleOtherLanguageSelection}
                disabled={needsConfirmation}
              >
                Select other language
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={handleLanguageConfirmation}
                isLoading={needsConfirmation}
              >
                Confirm Multilingual
              </Button>
            </div>
          </div>
        </SettingsSection.Item>
      )}
    </>
  );
});

const REMINDER_OFF = 'off' as const;

const REMINDER_OPTIONS: {
  value: MeetingReminderLeadTime | typeof REMINDER_OFF;
  label: string;
}[] = [
  { value: REMINDER_OFF, label: 'Off' },
  { value: MeetingReminderLeadTime.OneMinute, label: '1 minute before' },
  { value: MeetingReminderLeadTime.FiveMinutes, label: '5 minutes before' },
  { value: MeetingReminderLeadTime.TenMinutes, label: '10 minutes before' },
  { value: MeetingReminderLeadTime.ThirtyMinutes, label: '30 minutes before' },
  { value: MeetingReminderLeadTime.OneHour, label: '1 hour before' },
];

const MeetingReminderOption = observer(() => {
  const userConfig = useUserConfig();
  const selectedLabel = config.meetings.disable_reminders
    ? 'Off'
    : (REMINDER_OPTIONS.find(
        (opt) => opt.value === config.meetings.reminder_lead_time
      )?.label ?? '1 minute before');

  const selectReminder = (
    value: MeetingReminderLeadTime | typeof REMINDER_OFF
  ) => {
    if (value === REMINDER_OFF) {
      userConfig.meetings.patch({
        disable_reminders: true,
      });
      return;
    }
    userConfig.meetings.patch({
      disable_reminders: false,
      reminder_lead_time: value,
    });
  };

  return (
    <SettingsSection.Item searchId="meetings.reminder">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Remind me before meetings
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          How early to nudge you before a meeting starts
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{selectedLabel}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[12rem] gap-2 mt-1"
        >
          {REMINDER_OPTIONS.map((option) => (
            <DropdownMenu.Item
              key={String(option.value)}
              onSelect={() => selectReminder(option.value)}
            >
              {option.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const AutomaticMeetingPrepOption = observer(() => {
  const userConfig = useUserConfig();
  const automaticMeetingPrepEnabled = useAutomaticMeetingPrepEnabled();

  return (
    <SettingsSection.Item searchId="meetings.automatic-prep">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Prepare for meetings automatically
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Generate a prep brief before each meeting
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Switch
        checked={automaticMeetingPrepEnabled}
        onCheckedChange={(enabled) =>
          userConfig.meetings.set('automatic_meeting_prep_enabled', enabled)
        }
      />
    </SettingsSection.Item>
  );
});

const MeetingPrepLeadTimeOption = observer(() => {
  const userConfig = useUserConfig();
  const automaticMeetingPrepEnabled = useAutomaticMeetingPrepEnabled();
  const leadTimeMinutes = config.meetings.meeting_prep_lead_time;
  if (!automaticMeetingPrepEnabled) return null;

  return (
    <>
      <SettingsSection.Seperator />
      <SettingsSection.Item searchId="meetings.prep-lead-time">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Prepare meetings ahead by
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            How early the prep brief is generated
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <span>{formatMeetingPrepLeadTime(leadTimeMinutes)}</span>
            <Icons.chevronDown className="size-3" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            align="end"
            side="bottom"
            className="w-[10rem] gap-2 mt-1"
          >
            {MEETING_PREP_LEAD_TIME_PRESETS_MINUTES.map((minutes) => (
              <DropdownMenu.Item
                key={minutes}
                onSelect={() =>
                  userConfig.meetings.set('meeting_prep_lead_time', minutes)
                }
              >
                {formatMeetingPrepLeadTime(minutes)}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </SettingsSection.Item>
    </>
  );
});

const MeetingPrepEmailOption = observer(() => {
  const userConfig = useUserConfig();
  return (
    <SettingsSection.Item searchId="meetings.prep-email">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Email me meeting prep
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Send the prep brief to your inbox when it&apos;s ready
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Switch
        aria-label="Email me meeting prep"
        checked={config.meetings.meeting_prep_email_enabled}
        onCheckedChange={(enabled) =>
          userConfig.meetings.set('meeting_prep_email_enabled', enabled)
        }
      />
    </SettingsSection.Item>
  );
});

const MeetingSummaryEmailOption = observer(() => {
  const userConfig = useUserConfig();
  return (
    <SettingsSection.Item searchId="meetings.summary-email">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Email me meeting summaries
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Send the summary to your inbox after each recorded meeting
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Switch
        aria-label="Email me meeting summaries"
        checked={config.meetings.meeting_summary_email_enabled}
        onCheckedChange={(enabled) =>
          userConfig.meetings.set('meeting_summary_email_enabled', enabled)
        }
      />
    </SettingsSection.Item>
  );
});

const PreferredMeetingInputDeviceOption = observer(() => {
  return (
    <SettingsSection.Item searchId="meetings.microphone">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Microphone</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Pin a mic for meetings, or follow the system default
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <TranscriptionInputDeviceDropdown
        config={MEETING_PREFERENCE_INPUT_DEVICE_CONFIG}
      />
    </SettingsSection.Item>
  );
});

const ExcludeSoloEventsOption = observer(() => {
  const userConfig = useUserConfig();
  return (
    <SettingsSection.Item searchId="meetings.ignore-solo-events">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Ignore solo events
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Leave events with no other guests out of meetings, prep, and
          notifications
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Switch
        checked={config.meetings.exclude_solo_events}
        onCheckedChange={(v) =>
          userConfig.meetings.set('exclude_solo_events', v)
        }
      />
    </SettingsSection.Item>
  );
});

const DETECTION_MODE_OPTIONS = [
  MeetingDetectionMode.DoNothing,
  MeetingDetectionMode.Notify,
  MeetingDetectionMode.StartRecording,
];

const MeetingDetectionModeOption = observer(() => {
  const userConfig = useUserConfig();
  const selectedMode = config.meetings.disable_detection_notification
    ? MeetingDetectionMode.DoNothing
    : config.meetings.auto_start_on_detection
      ? MeetingDetectionMode.StartRecording
      : MeetingDetectionMode.Notify;

  const selectMode = (mode: MeetingDetectionMode) => {
    const previous = config.meetings.auto_start_on_detection;
    const autoStart = mode === MeetingDetectionMode.StartRecording;

    userConfig.meetings.patch({
      disable_detection_notification: mode === MeetingDetectionMode.DoNothing,
      auto_start_on_detection: autoStart,
    });

    if (previous !== autoStart) {
      electronApis.meetings?.logAutoStartEvent?.('settings_changed', {
        key: 'meeting_auto_start_on_detection',
        previous_value: previous,
        new_value: autoStart,
        trigger: 'user_toggle',
      });
    }
  };
  return (
    <SettingsSection.Item searchId="meetings.detection-mode">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          When a meeting app is detected
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          What Littlebird does the moment it spots a meeting
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{getMeetingDetectionModeLabel(selectedMode)}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[12rem] gap-2 mt-1"
        >
          {DETECTION_MODE_OPTIONS.map((mode) => (
            <DropdownMenu.Item key={mode} onSelect={() => selectMode(mode)}>
              {getMeetingDetectionModeLabel(mode)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const MeetingDetectionAppsOption = observer(() => {
  const meetingAppBundlesPayload = useFeaturePayload<unknown>(
    PAYLOAD_FEATURES.MEETING_APP_BUNDLE_IDS
  );
  const appGroups = groupMeetingAppsByName(
    resolveBaseMeetingApps(meetingAppBundlesPayload)
  );

  const disabledKeys = new Set(settingsStore.disabledMeetingAppBundleKeys);
  const isGroupExcluded = (keys: string[]) =>
    keys.every((key) => disabledKeys.has(key));

  const toOption = (group: { name: string }) => ({
    value: group.name,
    label: group.name,
  });

  const setGroupExcluded = (name: string, excluded: boolean) => {
    const group = appGroups.find((appGroup) => appGroup.name === name);
    if (group) {
      settingsStore.setMeetingAppGroupDisabled(group.keys, excluded);
    }
  };

  const isDetectionDisabled = config.meetings.disable_detection_notification;

  return (
    <SettingsSection.Item
      direction="vertical"
      searchId="meetings.excluded-apps"
    >
      <YStack
        className={
          isDetectionDisabled
            ? 'w-full gap-2 opacity-50 pointer-events-none'
            : 'w-full gap-2'
        }
      >
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Excluded meeting apps
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Littlebird won&apos;t detect meetings in these apps
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <MultiSelectSearchableDropdown
          options={appGroups
            .filter((group) => !isGroupExcluded(group.keys))
            .map(toOption)}
          selected={appGroups
            .filter((group) => isGroupExcluded(group.keys))
            .map(toOption)}
          onSelect={(name) => setGroupExcluded(name, true)}
          onRemove={(name) => setGroupExcluded(name, false)}
          onClearAll={() => settingsStore.clearDisabledMeetingAppKeys()}
          placeholder="No excluded apps"
          searchPlaceholder="Search apps..."
          emptyResult="No apps found"
        />
      </YStack>
    </SettingsSection.Item>
  );
});

const MeetingDetectionSitesOption = observer(() => {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const disabledHosts = settingsStore.disabledMeetingSiteHosts;
  const isDetectionDisabled = config.meetings.disable_detection_notification;

  const addHost = () => {
    const validationResult = domainSchema.safeParse(inputValue);
    if (!validationResult.success) {
      setInputError(
        validationResult.error.issues[0]?.message ||
          'Please enter a valid domain name.'
      );
      return;
    }
    if (disabledHosts.includes(validationResult.data)) {
      setInputError('Website is already excluded.');
      return;
    }
    settingsStore.addDisabledMeetingSiteHost(validationResult.data);
    setInputValue('');
    setInputError(null);
  };

  return (
    <SettingsSection.Item
      direction="vertical"
      searchId="meetings.excluded-sites"
    >
      <YStack
        className={
          isDetectionDisabled
            ? 'w-full gap-4 opacity-50 pointer-events-none'
            : 'w-full gap-4'
        }
      >
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Excluded websites
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Littlebird won&apos;t detect a meeting while your browser is on one
            of these websites
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <XStack className="gap-2 items-center w-full">
          <Input
            type="text"
            placeholder="Add website (example.com)"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setInputError(null);
            }}
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter') addHost();
            }}
          />
          <Button
            variant="outline"
            className="text-sm whitespace-nowrap"
            onClick={addHost}
            disabled={!inputValue.trim()}
          >
            Add
          </Button>
        </XStack>
        {inputError && (
          <div className="text-danger-9 text-xs">{inputError}</div>
        )}
        <ScrollArea className="max-h-[440px] bg-surface border border-border rounded-lg py-2 px-2">
          <YStack className="gap-1">
            {disabledHosts.length === 0 ? (
              <div className="text-xs text-foreground-muted p-2">
                No websites excluded yet.
              </div>
            ) : (
              disabledHosts.map((host) => (
                <XStack
                  key={host}
                  className="items-center justify-between rounded px-2 py-1 gap-2 bg-surface-muted"
                >
                  <XStack className="items-center gap-2 flex-1 min-w-0">
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${host}&sz=32`}
                      alt={`${host} favicon`}
                      className="size-4 shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <span className="text-sm truncate">{host}</span>
                  </XStack>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() =>
                      settingsStore.removeDisabledMeetingSiteHost(host)
                    }
                    className="text-danger-8 px-1 py-0 shrink-0"
                    aria-label={`Remove ${host}`}
                  >
                    <Icons.cancel className="size-3" />
                  </Button>
                </XStack>
              ))
            )}
          </YStack>
        </ScrollArea>
      </YStack>
    </SettingsSection.Item>
  );
});

const MeetingAutoStopTimeoutOption = observer(() => {
  const userConfig = useUserConfig();
  const timeoutOptions = [
    { value: MeetingAutoStopTimeout.Never, label: 'Never' },
    { value: MeetingAutoStopTimeout.FiveMinutes, label: 'After 5 minutes' },
    { value: MeetingAutoStopTimeout.FifteenMinutes, label: 'After 15 minutes' },
  ];

  const selectedLabel =
    timeoutOptions.find(
      (opt) => opt.value === config.meetings.auto_stop_timeout
    )?.label || 'After 15 minutes';

  return (
    <SettingsSection.Item searchId="meetings.auto-stop">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Stop recording after inactivity
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          End the recording once a meeting goes quiet
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{selectedLabel}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[10rem] gap-2 mt-1"
        >
          {timeoutOptions.map((option) => (
            <DropdownMenu.Item
              key={option.value}
              onSelect={() =>
                userConfig.meetings.set('auto_stop_timeout', option.value)
              }
            >
              {option.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const ShowSummaryAfterAutoStopOption = observer(() => {
  const userConfig = useUserConfig();
  const enabled = config.meetings.show_summary_after_auto_stop ?? true;
  return (
    <SettingsSection.Item searchId="meetings.show-summary-after-auto-stop">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Show summary when a meeting ends
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Open the summary when a meeting ends, without interrupting your typing
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Switch
        aria-label="Show summary when a meeting ends"
        checked={enabled}
        onCheckedChange={(nextEnabled) => {
          trackEvent('MEETING_AUTO_STOP_SUMMARY_SETTING_CHANGED', {
            previous_value: enabled,
            new_value: nextEnabled,
          });
          userConfig.meetings.set('show_summary_after_auto_stop', nextEnabled);
        }}
      />
    </SettingsSection.Item>
  );
});

const CALENDAR_DAYS_AHEAD_OPTIONS = [5, 7, 10];
const CALENDAR_MAX_EVENTS_OPTIONS = [10, 20, 30, 50];

const CalendarDaysAheadOption = observer(() => {
  const userConfig = useUserConfig();
  const selected = config.calendar.days_ahead;

  const handleSelect = (days: number) => {
    userConfig.calendar.set('days_ahead', days);
    const validOptions = Object.values(
      MeetingMenubarIncludeEventsOption
    ).filter((option) => getMeetingMenubarIncludeEventsDays(option) <= days);
    const currentInclude = config.menubar.include_events as
      | MeetingMenubarIncludeEventsOption
      | undefined;
    if (!currentInclude || !validOptions.includes(currentInclude)) {
      userConfig.menubar.set(
        'include_events',
        validOptions[validOptions.length - 1]
      );
    }
  };

  return (
    <SettingsSection.Item searchId="meetings.calendar-look-ahead">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Calendar Look-Ahead Window
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          How far in advance to fetch upcoming calendar events
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{selected} Days</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[10rem] gap-2 mt-1"
        >
          {CALENDAR_DAYS_AHEAD_OPTIONS.map((days) => (
            <DropdownMenu.Item key={days} onSelect={() => handleSelect(days)}>
              {days} Days
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const CalendarMaxEventsOption = observer(() => {
  const userConfig = useUserConfig();
  const selected = config.calendar.max_events;

  return (
    <SettingsSection.Item searchId="meetings.calendar-max-events">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Max Calendar Events
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Maximum calendar events to fetch within the look-ahead window
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{selected}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[10rem] gap-2 mt-1"
        >
          {CALENDAR_MAX_EVENTS_OPTIONS.map((max) => (
            <DropdownMenu.Item
              key={max}
              onSelect={() => userConfig.calendar.set('max_events', max)}
            >
              {max}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const BASE_EVENT_TYPE_OPTIONS: { value: EventType; label: string }[] = [
  { value: EventType.WorkingLocation, label: 'Working Location' },
  { value: EventType.OutOfOffice, label: 'Out of Office' },
  { value: EventType.Birthday, label: 'Birthdays' },
  { value: EventType.FocusTime, label: 'Focus Time' },
];

const ExcludedCalendarEventTypesOption = observer(() => {
  const userConfig = useUserConfig();
  const { data: gcalAccounts } = useConnectedAccountsQuery('googlecalendar');
  const hasGcal = gcalAccounts?.some(
    (a) => a.connection_status === 'connected'
  );
  const eventTypeOptions = hasGcal
    ? [
        ...BASE_EVENT_TYPE_OPTIONS,
        { value: EventType.FromGmail, label: 'Autogenerated from Gmail' },
      ]
    : BASE_EVENT_TYPE_OPTIONS;
  const selected = config.calendar.excluded_event_types as EventType[];
  const toggle = (type: EventType) => {
    const next = selected.includes(type)
      ? selected.filter((t) => t !== type)
      : [...selected, type];
    userConfig.calendar.set('excluded_event_types', next);
  };
  const summaryLabel =
    selected.length === 0 ? 'None' : `${selected.length} excluded`;

  return (
    <SettingsSection.Item searchId="meetings.excluded-event-types">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Excluded Event Types
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Calendar event types to hide from meeting notes, the menu bar, and
          Home
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{summaryLabel}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[12rem] gap-1 mt-1"
        >
          {eventTypeOptions.map((option) => (
            <DropdownMenu.CheckboxItem
              key={option.value}
              checked={selected.includes(option.value)}
              onCheckedChange={() => toggle(option.value)}
              onSelect={(event) => event.preventDefault()}
            >
              {option.label}
            </DropdownMenu.CheckboxItem>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'tentative', label: 'Tentative' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ExcludedCalendarStatusesOption = observer(() => {
  const userConfig = useUserConfig();
  const selected = config.calendar.excluded_statuses;
  const toggle = (status: string) => {
    const next = selected.includes(status)
      ? selected.filter((s) => s !== status)
      : [...selected, status];
    userConfig.calendar.set('excluded_statuses', next);
  };
  const summaryLabel =
    selected.length === 0 ? 'None' : `${selected.length} excluded`;

  return (
    <SettingsSection.Item searchId="meetings.excluded-statuses">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Excluded Statuses</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Calendar event statuses to hide from meeting notes, the menu bar, and
          Home
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{summaryLabel}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[12rem] gap-1 mt-1"
        >
          {STATUS_OPTIONS.map((option) => (
            <DropdownMenu.CheckboxItem
              key={option.value}
              checked={selected.includes(option.value)}
              onCheckedChange={() => toggle(option.value)}
              onSelect={(event) => event.preventDefault()}
            >
              {option.label}
            </DropdownMenu.CheckboxItem>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const MenubarCalendarEnabledOption = observer(() => {
  const userConfig = useUserConfig();
  return (
    <SettingsSection.Item searchId="meetings.menubar-calendar">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Menu bar calendar</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Show upcoming calendar events in the macOS menu bar
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Switch
        checked={config.menubar.calendar_enabled}
        onCheckedChange={(value) =>
          userConfig.menubar.set('calendar_enabled', value)
        }
      />
    </SettingsSection.Item>
  );
});

const MenubarIncludeEventsOptionField = observer(() => {
  const userConfig = useUserConfig();
  const daysAhead = config.calendar.days_ahead;
  const options = [
    MeetingMenubarIncludeEventsOption.Today,
    MeetingMenubarIncludeEventsOption.TodayAndTomorrow,
    MeetingMenubarIncludeEventsOption.ThreeDays,
    MeetingMenubarIncludeEventsOption.OneWeek,
  ].filter((o) => getMeetingMenubarIncludeEventsDays(o) <= daysAhead);

  return (
    <SettingsSection.Item searchId="meetings.menubar-include-events">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Show events from</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          How far ahead the menu bar calendar lists events
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>
            {getMeetingMenubarIncludeEventsLabel(
              config.menubar.include_events as MeetingMenubarIncludeEventsOption
            )}
          </span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[14rem] gap-2 mt-1"
        >
          {options.map((option) => (
            <DropdownMenu.Item
              key={option}
              onSelect={() => userConfig.menubar.set('include_events', option)}
            >
              {getMeetingMenubarIncludeEventsLabel(option)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const MenubarPreviewLeadTimeOptionField = observer(() => {
  const userConfig = useUserConfig();
  const options = [
    MeetingMenubarPreviewLeadTimeOption.Never,
    MeetingMenubarPreviewLeadTimeOption.AtStartOfEvent,
    MeetingMenubarPreviewLeadTimeOption.ThirtyMinutes,
    MeetingMenubarPreviewLeadTimeOption.ThreeHours,
    MeetingMenubarPreviewLeadTimeOption.Always,
  ];

  return (
    <SettingsSection.Item searchId="meetings.menubar-preview-lead-time">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Preview the next event
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          When the next event appears in the menu bar chip
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>
            {getMeetingMenubarPreviewLeadTimeLabel(
              config.menubar
                .preview_lead_time as MeetingMenubarPreviewLeadTimeOption
            )}
          </span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-[16rem] gap-2 mt-1"
        >
          {options.map((option) => (
            <DropdownMenu.Item
              key={option}
              onSelect={() =>
                userConfig.menubar.set('preview_lead_time', option)
              }
            >
              {getMeetingMenubarPreviewLeadTimeLabel(option)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const AssistInstructionsItem = observer(
  ({ fields }: { fields: MeetingPromptFields }) => {
    const hasFeature = useFeature();
    if (!hasFeature(FEATURES.MEETING_ASSIST)) return null;

    return (
      <>
        <SettingsSection.Seperator />
        <SettingsSection.Item
          direction="vertical"
          searchId="meetings.assist-instructions"
        >
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Meeting Assist
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Tell Littlebird what to suggest during your meetings, and what to
              leave out.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <MeetingAssistInstructionsTextArea
            className="min-h-[8rem] max-h-[16rem] text-sm resize-none [field-sizing:content]"
            fields={fields}
          />
        </SettingsSection.Item>
      </>
    );
  }
);

const MeetingAssistGroup = observer(() => {
  const hasFeature = useFeature();
  if (!hasFeature(FEATURES.MEETING_ASSIST)) return null;

  return (
    <SettingsSection.Group title="Meeting Assist">
      <SettingsSection.Item searchId="meetings.assist">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Turn on Meeting Assist
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Answer questions and draft follow-ups as your meeting runs
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <Switch
          aria-label="Turn on Meeting Assist"
          checked={resolveMeetingAssistEnabled(
            config.meetings.meeting_assist_enabled
          )}
          onCheckedChange={(enabled) =>
            setMeetingAssistEnabled(enabled, 'settings')
          }
        />
      </SettingsSection.Item>
      {hasFeature(FEATURES.MEETING_ASSIST_POPUPS) && (
        <MeetingAssistPopupOptions />
      )}
    </SettingsSection.Group>
  );
});

const MeetingIntelligenceGroup = observer(() => {
  const hasFeature = useFeature();
  const userConfig = useUserConfig();
  if (!hasFeature(FEATURES.MEETING_INTENT_DETECTION)) return null;

  return (
    <SettingsSection.Group title="Meeting Intelligence">
      <SettingsSection.Item searchId="meetings.intelligence">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Meeting Intelligence
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Automatically catch action items and follow-ups as they come up in
            your meetings
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <Switch
          aria-label="Meeting Intelligence"
          checked={resolveMeetingIntelligenceEnabled(
            config.meetings.meeting_intelligence_enabled
          )}
          onCheckedChange={(enabled) =>
            userConfig.meetings.set('meeting_intelligence_enabled', enabled)
          }
        />
      </SettingsSection.Item>
    </SettingsSection.Group>
  );
});

const CustomVocabularyOption = observer(() => {
  const hasFeature = useFeature();
  const [input, setInput] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const vocabulary = userStore.user?.custom_transcription_vocabulary;

  useEffect(() => {
    if (!isEditing) {
      setInput(vocabulary?.join(', ') ?? '');
    }
  }, [vocabulary, isEditing]);

  // One save in flight at a time; concurrent PATCHes can land out of order
  const queuedVocabularyRef = useRef<string[] | null>(null);

  const { mutate: saveVocabulary, isPending } = useMutation({
    mutationKey: ['update-custom-transcription-vocabulary'],
    mutationFn: async (newVocabulary: string[]) =>
      backendApis.user.setCustomTranscriptionVocabulary(newVocabulary),
    onSuccess: (_, newVocabulary) => {
      userStore.updateUser({ custom_transcription_vocabulary: newVocabulary });
    },
    onError: () => {
      toast.error("Couldn't save your custom vocabulary", {
        description: 'Your edits are still here. Try again.',
      });
    },
    onSettled: (_, error) => {
      const queuedVocabulary = queuedVocabularyRef.current;
      queuedVocabularyRef.current = null;
      if (queuedVocabulary) {
        saveVocabulary(queuedVocabulary);
      } else if (!error) {
        setIsEditing(false);
      }
    },
  });

  if (!hasFeature(FEATURES.MEETING_NOTES_CUSTOM_VOCABULARY)) return null;

  const handleSave = () => {
    const newVocabulary = input
      .split(',')
      .map((word) => word.trim())
      .filter(Boolean);

    if (isPending) {
      queuedVocabularyRef.current = newVocabulary;
      return;
    }
    if (JSON.stringify(newVocabulary) === JSON.stringify(vocabulary ?? [])) {
      setIsEditing(false);
      return;
    }
    saveVocabulary(newVocabulary);
  };

  return (
    <>
      <SettingsSection.Seperator />
      <SettingsSection.Item
        direction="vertical"
        searchId="meetings.custom-vocabulary"
      >
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Custom vocabulary
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Littlebird uses these when transcribing. Add company names, project
            names, or other terms. Comma-separate words.
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <TextArea
          className="h-24 text-sm resize-none mt-2"
          placeholder="Acme Corp, Project Phoenix, JIRA ..."
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setIsEditing(true);
          }}
          onBlur={handleSave}
        />
      </SettingsSection.Item>
    </>
  );
});
